import React from "react"

import { VolumeVarianceConfig } from "@/metrics/registry/configuration"
import type { AudioRegionReviewConfig } from "@/metrics/runtime"
import { Tooltip } from "@coval/ui"
import { Info } from "lucide-react"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { getStat } from "../helpers/getStat"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

const CATEGORY = "volume_variance"

const LOUD_COLOR = "rgba(239, 68, 68, 0.3)"
const SOFT_COLOR = "rgba(255, 165, 0, 0.3)"

/** Minimum region duration (seconds) used when a region has zero length (marker-style). Mirrors useAudioRegionReview. */
const MIN_REGION_DURATION_SEC = 0.05

// ---------------------------------------------------------------------------
// Audio dB computation utilities
// ---------------------------------------------------------------------------

/** Compute RMS amplitude in dBFS for a time range within an AudioBuffer. */
const computeSegmentDbfs = (
  audioBuffer: AudioBuffer,
  startTime: number,
  endTime: number,
): number => {
  const sampleRate = audioBuffer.sampleRate
  const startSample = Math.max(0, Math.floor(startTime * sampleRate))
  const endSample = Math.min(
    audioBuffer.length,
    Math.ceil(endTime * sampleRate),
  )
  if (endSample <= startSample) {
    return -Infinity
  }

  let sumSquares = 0
  const numChannels = audioBuffer.numberOfChannels
  for (let ch = 0; ch < numChannels; ch++) {
    const data = audioBuffer.getChannelData(ch)
    for (let i = startSample; i < endSample; i++) {
      sumSquares += data[i] * data[i]
    }
  }
  const rms = Math.sqrt(sumSquares / ((endSample - startSample) * numChannels))
  return rms > 0 ? 20 * Math.log10(rms) : -Infinity
}

/** Compute std deviation from an array of numbers (ignoring -Infinity). */
const computeStdDeviation = (values: number[]): number => {
  const finite = values.filter((v) => isFinite(v))
  if (finite.length === 0) {
    return 0
  }
  const mean = finite.reduce((a, b) => a + b, 0) / finite.length
  const variance =
    finite.reduce((sum, v) => sum + (v - mean) ** 2, 0) / finite.length
  return Math.sqrt(variance)
}

/**
 * Given an AudioBuffer and labeled regions, compute the volume variance
 * (std deviation of per-segment dBFS) and per-segment breakdown.
 */
export const computeVolumeVarianceFromRegions = (
  audioBuffer: AudioBuffer,
  regions: AudioRegionParams[],
): {
  stdDeviationDb: number
  loudCount: number
  softCount: number
  segmentDbs: number[]
} => {
  const segmentDbs = regions.map((r) => {
    const end =
      r.end != null && r.end !== r.start
        ? r.end
        : r.start + MIN_REGION_DURATION_SEC
    return computeSegmentDbfs(audioBuffer, r.start, end)
  })
  const stdDeviationDb = computeStdDeviation(segmentDbs)
  const loudCount = regions.filter((r) => r.color === LOUD_COLOR).length
  const softCount = regions.filter((r) => r.color === SOFT_COLOR).length
  return { stdDeviationDb, loudCount, softCount, segmentDbs }
}

const STAT_NAME = "scoped_volume_db"
// Old rows still carry the previous stat name.
const LEGACY_STAT_NAME = "agent_volume_db"

interface VolumeVarianceResultJson {
  metadata?: {
    preset?: string
  }
  thresholds?: {
    loud_threshold_db?: number
    soft_threshold_db?: number
  }
  statistics?: {
    std_deviation_db?: number
    mean_db?: number
    max_db?: number
    min_db?: number
    dynamic_range_db?: number
  }
  segment_analysis?: {
    loud_count?: number
    soft_count?: number
    total_intervals?: number
  }
  subvalues_by_timestamp?: SubvalueByTimestamp[]
  timestamps?: Array<{ start_time: number; end_time: number; label: string }>
  // Typed raw_values shape (preset, thresholds, intervals) for new metric outputs.
  loud_threshold_db?: number
  soft_threshold_db?: number
  preset?: string
  interval_seconds?: number
  db_intervals?: Array<{ start_time: number; end_time: number; db?: number }>
}

export class VolumeVarianceMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = "dB"
  aggregate_units = "dB"

  get hasVersionedConfig() {
    return true
  }

  isMetricOutputCurrentVersion(metricOutput: {
    result_json?: VolumeVarianceResultJson | null
  }): boolean {
    const resultJson = metricOutput.result_json
    if (!resultJson?.metadata?.preset && !resultJson?.thresholds) {
      return true
    }
    const currentPreset = this.metric_metadata?.threshold_preset || "normal"
    const executionPreset = resultJson.metadata?.preset
    if (executionPreset && currentPreset !== executionPreset) {
      return false
    }
    if (currentPreset === "custom" && resultJson.thresholds) {
      if (
        this.metric_metadata?.loud_threshold_db != null &&
        resultJson.thresholds.loud_threshold_db != null &&
        this.metric_metadata.loud_threshold_db !==
          resultJson.thresholds.loud_threshold_db
      ) {
        return false
      }
      if (
        this.metric_metadata?.soft_threshold_db != null &&
        resultJson.thresholds.soft_threshold_db != null &&
        this.metric_metadata.soft_threshold_db !==
          resultJson.thresholds.soft_threshold_db
      ) {
        return false
      }
    }
    return true
  }

  getVersionDiff(metricOutput: {
    result_json?: VolumeVarianceResultJson | null
  }): Array<{ field: string; current: string; previous: string }> {
    const diffs: Array<{ field: string; current: string; previous: string }> =
      []
    const resultJson = metricOutput.result_json
    if (!resultJson?.metadata?.preset && !resultJson?.thresholds) {
      return diffs
    }
    const currentPreset = this.metric_metadata?.threshold_preset || "normal"
    const executionPreset = resultJson?.metadata?.preset
    if (executionPreset && currentPreset !== executionPreset) {
      diffs.push({
        field: "Threshold Preset",
        current: currentPreset,
        previous: executionPreset,
      })
    }
    if (currentPreset === "custom" && resultJson?.thresholds) {
      if (
        this.metric_metadata?.loud_threshold_db != null &&
        resultJson.thresholds.loud_threshold_db != null &&
        this.metric_metadata.loud_threshold_db !==
          resultJson.thresholds.loud_threshold_db
      ) {
        diffs.push({
          field: "Loud Threshold (dB)",
          current: String(this.metric_metadata.loud_threshold_db),
          previous: String(resultJson.thresholds.loud_threshold_db),
        })
      }
      if (
        this.metric_metadata?.soft_threshold_db != null &&
        resultJson.thresholds.soft_threshold_db != null &&
        this.metric_metadata.soft_threshold_db !==
          resultJson.thresholds.soft_threshold_db
      ) {
        diffs.push({
          field: "Soft Threshold (dB)",
          current: String(this.metric_metadata.soft_threshold_db),
          previous: String(resultJson.thresholds.soft_threshold_db),
        })
      }
    }
    return diffs
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    interface SummaryStat {
      min?: number
      max?: number
      mean?: number
      median?: number
    }
    interface DispersionStat {
      std_dev?: number
    }
    interface OutlierStat {
      count?: number
      number_of_data_points?: number
    }
    const summary =
      getStat<SummaryStat>(mo, "summary", STAT_NAME) ??
      getStat<SummaryStat>(mo, "summary", LEGACY_STAT_NAME)
    const dispersion =
      getStat<DispersionStat>(mo, "dispersion", STAT_NAME) ??
      getStat<DispersionStat>(mo, "dispersion", LEGACY_STAT_NAME)
    const outliers =
      getStat<OutlierStat>(mo, "outliers", STAT_NAME) ??
      getStat<OutlierStat>(mo, "outliers", LEGACY_STAT_NAME)

    const rawValues = parseResultJson<VolumeVarianceResultJson>(mo)
    const legacy = parseResultJson<VolumeVarianceResultJson>(mo.result_json)

    const stdDeviation =
      dispersion?.std_dev ??
      legacy?.statistics?.std_deviation_db ??
      mo.float_value ??
      0

    // Typed stats expose a combined outlier count; derive loud/soft from
    // subvalues (new) or fall back to legacy segment_analysis (historical).
    const subvalues = mo.subvalues_by_timestamp ?? []
    const typedLoudCount = subvalues.filter((s) =>
      (s.string_value ?? s.output_type ?? "").toLowerCase().includes("loud"),
    ).length
    const typedSoftCount = subvalues.filter((s) =>
      (s.string_value ?? s.output_type ?? "").toLowerCase().includes("soft"),
    ).length
    const loudCount = legacy?.segment_analysis?.loud_count ?? typedLoudCount
    const softCount = legacy?.segment_analysis?.soft_count ?? typedSoftCount
    const totalIntervals =
      legacy?.segment_analysis?.total_intervals ??
      outliers?.number_of_data_points ??
      rawValues?.db_intervals?.length ??
      0
    const isLegacySegmentData = legacy?.segment_analysis != null
    const outlierCount = outliers?.count

    const preset = rawValues?.preset ?? legacy?.metadata?.preset ?? "normal"
    const loudThresholdDb =
      rawValues?.loud_threshold_db ?? legacy?.thresholds?.loud_threshold_db
    const softThresholdDb =
      rawValues?.soft_threshold_db ?? legacy?.thresholds?.soft_threshold_db

    const meanDb = summary?.mean ?? legacy?.statistics?.mean_db
    const maxDb = summary?.max ?? legacy?.statistics?.max_db
    const minDb = summary?.min ?? legacy?.statistics?.min_db
    const dynamicRangeDb =
      legacy?.statistics?.dynamic_range_db ??
      (maxDb != null && minDb != null ? maxDb - minDb : undefined)
    const hasStatistics =
      summary != null || dispersion != null || legacy?.statistics != null

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-surface-1">
            <p className="text-sm text-coval-text-muted">Volume Variance</p>
            <p className="text-2xl font-semibold">
              {stdDeviation.toFixed(2)} {this.unit}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-surface-1">
            <p className="text-sm text-coval-text-muted">Preset</p>
            <p className="text-lg font-medium capitalize">{preset}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 rounded-lg bg-surface-1">
            <p className="text-sm text-coval-text-muted">Loud Segments</p>
            <p className="text-xl font-semibold">{loudCount}</p>
            {loudThresholdDb !== undefined && (
              <p className="text-xs text-coval-text-muted">
                &gt; {loudThresholdDb} dBFS
              </p>
            )}
          </div>
          <div className="p-3 rounded-lg bg-surface-1">
            <p className="text-sm text-coval-text-muted">Soft Segments</p>
            <p className="text-xl font-semibold">{softCount}</p>
            {softThresholdDb !== undefined && (
              <p className="text-xs text-coval-text-muted">
                &lt; {softThresholdDb} dBFS
              </p>
            )}
          </div>
          <div className="p-3 rounded-lg bg-surface-1">
            <div className="flex items-center gap-1">
              <p className="text-sm text-coval-text-muted">
                {!isLegacySegmentData && outlierCount != null
                  ? "Outliers"
                  : "Total Intervals"}
              </p>
              <Tooltip
                content="The audio is divided into fixed-length segments. Each segment's volume is measured and compared against the loud/soft thresholds."
                side="top"
              >
                <button type="button" className="inline-flex" aria-label="Info">
                  <Info className="w-3.5 h-3.5 text-coval-text-muted" />
                </button>
              </Tooltip>
            </div>
            <p className="text-xl font-semibold">
              {!isLegacySegmentData && outlierCount != null
                ? outlierCount
                : totalIntervals}
            </p>
          </div>
        </div>

        {hasStatistics && (
          <div className="p-4 rounded-lg bg-surface-1">
            <p className="text-sm text-coval-text-muted mb-2">Statistics</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p>Mean: {meanDb != null ? meanDb.toFixed(2) : "N/A"} dBFS</p>
              <p>Max: {maxDb != null ? maxDb.toFixed(2) : "N/A"} dBFS</p>
              <p>
                Dynamic Range:{" "}
                {dynamicRangeDb != null ? dynamicRangeDb.toFixed(2) : "N/A"} dB
              </p>
              <p>Min: {minDb != null ? minDb.toFixed(2) : "N/A"} dBFS</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      const mapSubvalue = (
        item: SubvalueByTimestamp,
        idx: number,
      ): AudioRegionParams => {
        // string_value carries seg["threshold"] ("loud" / "soft") in the backend.
        // Fall back to output_type for old SubvalueByTimestamp format.
        const isLoud = (item.string_value ?? item.output_type ?? "")
          .toLowerCase()
          .includes("loud")
        return {
          id: `volume:${idx}`,
          start: item.start_offset,
          end: Math.max(item.end_offset, item.start_offset + 0.1),
          color: isLoud
            ? "rgba(239, 68, 68, 0.3)" // red — loud
            : "rgba(255, 165, 0, 0.3)", // orange — soft
          drag: false,
          resize: false,
        }
      }

      // Prefer top-level subvalues_by_timestamp (new format)
      const directSubvalues = mo.subvalues_by_timestamp ?? []
      if (directSubvalues.length > 0) {
        return directSubvalues.map(mapSubvalue)
      }

      // @deprecated fallback: result_json.subvalues_by_timestamp — remove after subvalues_by_timestamp migration is complete
      const resultData = parseResultJson<VolumeVarianceResultJson>(
        mo.result_json,
      )
      if (Array.isArray(resultData?.subvalues_by_timestamp)) {
        return resultData.subvalues_by_timestamp.map(mapSubvalue)
      }

      // @deprecated fallback: result_json.timestamps — remove after subvalues_by_timestamp migration is complete
      const timestamps = resultData?.timestamps
      if (!Array.isArray(timestamps)) {
        return []
      }
      return timestamps.map(
        (
          ts: { start_time: number; end_time: number; label: string },
          idx: number,
        ) => {
          const isLoud = ts.label?.toLowerCase().includes("loud")
          return {
            id: `volume:${idx}`,
            start: ts.start_time,
            end: Math.max(ts.end_time, ts.start_time + 0.1),
            color: isLoud
              ? "rgba(239, 68, 68, 0.3)" // red — loud
              : "rgba(255, 165, 0, 0.3)", // orange — soft
            drag: false,
            resize: false,
          }
        },
      )
    } catch {
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "raw_count",
      regionLabels: [
        { value: "loud", label: "Loud", color: LOUD_COLOR },
        { value: "soft", label: "Quiet", color: SOFT_COLOR },
      ],
      buildReviewedResultJson: ({ score, regions }) => {
        const loudCount = regions.filter((r) => r.color === LOUD_COLOR).length
        const softCount = regions.filter((r) => r.color === SOFT_COLOR).length
        return {
          std_deviation_db: Number(score.toFixed(2)),
          loud_count: loudCount,
          soft_count: softCount,
        }
      },
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
  ): React.ReactNode {
    return (
      <div className="flex flex-col gap-4">
        <VolumeVarianceConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(metricRecordData, onChange, textAreaRef)}
      </div>
    )
  }
}
